// Vergleich zweier Textfassungen fuer die Aenderungsanzeige der Dokumentation.
//
// Bewusst ohne Fremdbibliothek: ein Wort-Diff ueber LCS sind wenige Zeilen, und eine neue
// Abhaengigkeit in der CRA-Toolchain waere fuer diesen Zweck unverhaeltnismaessig.
//
// Grundsatz: der Vergleich BEHAUPTET nichts. Er zeigt, was tatsaechlich verschieden ist. Wo
// zwei Fassungen gleich sind, wird nichts hervorgehoben.

// Zerlegt in Woerter samt Trennzeichen, damit der Rueckbau zeichengleich ist.
const zerlege = (t) => String(t || "").split(/(\s+)/).filter(x => x !== "");

// Laengste gemeinsame Teilfolge, klassisch. Fuer Abschnittsgroessen (einige tausend Woerter)
// unkritisch; oberhalb einer Grenze wird auf einen Blockvergleich zurueckgefallen.
const LCS_GRENZE = 1200;

export function wortDiff(vorher, nachher) {
  const a = zerlege(vorher), b = zerlege(nachher);
  if (a.length > LCS_GRENZE || b.length > LCS_GRENZE) {
    // Zu gross fuer LCS: gemeinsamen Anfang und Ende abschaelen, den Rest als Block melden.
    let s = 0;
    while (s < a.length && s < b.length && a[s] === b[s]) s++;
    let e = 0;
    while (e < a.length - s && e < b.length - s && a[a.length - 1 - e] === b[b.length - 1 - e]) e++;
    const teile = [];
    if (s) teile.push({ typ: "gleich", text: a.slice(0, s).join("") });
    const weg = a.slice(s, a.length - e).join("");
    const neu = b.slice(s, b.length - e).join("");
    if (weg) teile.push({ typ: "weg", text: weg });
    if (neu) teile.push({ typ: "neu", text: neu });
    if (e) teile.push({ typ: "gleich", text: a.slice(a.length - e).join("") });
    return verdichte(teile);
  }
  // LCS-Tabelle
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const roh = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { roh.push({ typ: "gleich", text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { roh.push({ typ: "weg", text: a[i] }); i++; }
    else { roh.push({ typ: "neu", text: b[j] }); j++; }
  }
  while (i < n) { roh.push({ typ: "weg", text: a[i++] }); }
  while (j < m) { roh.push({ typ: "neu", text: b[j++] }); }
  return verdichte(roh);
}

// Gleichartige Nachbarn zusammenfassen. Zusaetzlich: ein reines Leerzeichen zwischen zwei
// gleichartigen Aenderungen wird eingemeindet. Ohne das zerreisst ein geteiltes Leerzeichen die
// Hervorhebung ("Secure" + " " + "Store" statt "Secure Store") - im Test aufgefallen.
function verdichte(roh) {
  const t1 = [];
  for (let i = 0; i < roh.length; i++) {
    const t = roh[i];
    if (t.typ === "gleich" && /^\s+$/.test(t.text)) {
      const davor = t1[t1.length - 1], danach = roh[i + 1];
      if (davor && danach && davor.typ === danach.typ && davor.typ !== "gleich") {
        davor.text += t.text;
        continue;
      }
    }
    const letzte = t1[t1.length - 1];
    if (letzte && letzte.typ === t.typ) letzte.text += t.text;
    else t1.push({ ...t });
  }
  return t1;
}

// Welche ##-Abschnitte unterscheiden sich? Liefert die Indizes der NEUEN Fassung.
// Wichtig: derselbe Split wie im Renderer, damit die Indizes zur Anzeige passen.
export function geaenderteAbschnitte(vorher, nachher) {
  const a = String(vorher || "").split(/(?=^## )/gm);
  const b = String(nachher || "").split(/(?=^## )/gm);
  const raus = [];
  for (let i = 0; i < b.length; i++) {
    // Gleicher Index UND gleicher Inhalt heisst unveraendert. Verschiebt sich die Anzahl der
    // Abschnitte, gilt ab der ersten Abweichung alles als geaendert - das ist ehrlicher, als
    // eine Zuordnung zu erraten.
    // Rand-Leerzeichen sind keine inhaltliche Aenderung. Ohne diese Normalisierung wuerde ein
    // zusaetzlicher Zeilenumbruch am Abschnittsende einen unberuehrten Abschnitt als geaendert
    // markieren - im Test aufgefallen.
    const alt = a[i] === undefined ? undefined : String(a[i]).replace(/\s+$/, "");
    const neu = String(b[i]).replace(/\s+$/, "");
    if (alt === undefined || alt !== neu) raus.push(i);
  }
  return raus;
}

// ── Anker: Markierungen haengen am KAPITEL, nicht an seiner Position ─────────────────
//
// Warum: Abschnittsindizes verschieben sich, sobald ein Kapitel wegfaellt oder dazukommt, und
// die Nummerierung wird beim Aufraeumen neu gezogen. Eine ueber den Index gesetzte Markierung
// wandert dann auf ein Kapitel, das nie angefasst wurde - eine Falschaussage. Der Anker ist
// deshalb der Kapiteltitel OHNE Nummer; zusaetzlich zaehlen wir gleichnamige Titel durch.

export const ankerVon = (titel) => String(titel || "")
  .replace(/^#+\s*/, "")
  .replace(/^\d+(?:\.\d+)*\.?\s*/, "")
  .toLowerCase().replace(/\s+/g, " ").trim();

// Zerlegt wie der Renderer (gleicher Split) und gibt je Abschnitt Index, Titel und Schluessel.
export function zerlegeAbschnitte(md) {
  const teile = String(md || "").split(/(?=^## )/gm);
  const zaehler = new Map();
  return teile.map((text, index) => {
    const kopf = (text.match(/^##\s+(.+)$/m) || [])[1] || "";
    const anker = ankerVon(kopf);
    const n = (zaehler.get(anker) || 0) + 1;
    zaehler.set(anker, n);
    return { index, titel: kopf.trim(), anker, schluessel: `${anker}#${n}`, text };
  });
}

// Vergleichsform: die Nummer in der Ueberschrift wird ausgeblendet. Ein reines Umnummerieren
// ist keine inhaltliche Aenderung und darf keine Markierung setzen.
function ohneNummer(text) {
  const s = String(text || "").replace(/\s+$/, "");
  const i = s.indexOf("\n");
  const kopf = (i === -1 ? s : s.slice(0, i)).replace(/^(#+\s*)\d+(?:\.\d+)*\.?\s*/, "$1");
  return kopf + (i === -1 ? "" : s.slice(i));
}

// Welche Kapitel hat diese eine Aenderung angefasst? Liefert je Kapitel den Text davor und
// danach, damit das Popup spaeter genau diese Stelle zeigen kann - nicht das ganze Dokument.
export function stellenFuerRevision(vorher, nachher) {
  const alt = new Map(zerlegeAbschnitte(vorher).map(s => [s.schluessel, s]));
  const raus = [];
  for (const s of zerlegeAbschnitte(nachher)) {
    const v = alt.get(s.schluessel);
    if (v && ohneNummer(v.text) === ohneNummer(s.text)) continue;
    raus.push({ schluessel: s.schluessel, anker: s.anker, titel: s.titel,
      vorherText: v ? v.text : "", nachherText: s.text, neu: !v });
  }
  return raus;
}

// Wo liegen diese Stellen im AKTUELLEN Dokument? Findet ein Anker sich nicht mehr (Kapitel
// umbenannt oder entfernt), fehlt die Markierung - das ist ehrlicher als sie zu erraten.
export function loeseStellen(md, stellen) {
  const jetzt = new Map(zerlegeAbschnitte(md).map(s => [s.schluessel, s.index]));
  const raus = new Map();
  for (const st of (stellen || [])) {
    const idx = jetzt.get(st.schluessel);
    if (idx !== undefined) raus.set(idx, st);
  }
  return raus;
}

// Speicherform der Aenderungen. Eine Revision haelt im Arbeitsspeicher das GANZE Dokument vor
// und nach der Aenderung (je ~20 KB). 25 davon waeren rund 1 MB - und der Stand wird nach jeder
// Aenderung erneut gesichert. Gespeichert wird deshalb nur, was nach dem Neuladen gebraucht wird:
// die betroffenen Kapitel (dafuer sind die Markierung und das Popup da) plus die Vorfassung der
// JUENGSTEN Aenderung, denn nur die laesst sich zuruecknehmen.
export const REV_TEXT_MAX = 20000;
export function schlankeRevisionen(revisionen) {
  const liste = Array.isArray(revisionen) ? revisionen : [];
  return liste.map((r, i) => {
    const letzte = i === liste.length - 1;
    return {
      id: r.id, ts: r.ts, anweisung: r.anweisung, aktion: r.aktion,
      umfang: r.umfang, abschnitte: r.abschnitte,
      stellen: (r.stellen || []).map(s => ({
        schluessel: s.schluessel, anker: s.anker, titel: s.titel, neu: s.neu,
        vorherText: String(s.vorherText || "").slice(0, REV_TEXT_MAX),
        nachherText: String(s.nachherText || "").slice(0, REV_TEXT_MAX),
      })),
      // Nur die juengste traegt die Vorfassung - nur sie kann zurueckgenommen werden.
      ...(letzte ? { vorher: r.vorher } : {}),
    };
  });
}

// Kurzfassung fuer die Versionsliste: wie viel wurde entfernt, wie viel kam hinzu.
export function diffUmfang(vorher, nachher) {
  let neu = 0, weg = 0;
  for (const t of wortDiff(vorher, nachher)) {
    if (t.typ === "neu") neu += t.text.trim().length;
    else if (t.typ === "weg") weg += t.text.trim().length;
  }
  return { neu, weg };
}

/**
 * Repariert AUSSCHLIESSLICH kaputte Markdown-Syntax, die kein Mensch so gemeint haben kann.
 * Heute genau ein Fall: Ueberschriften ohne Leerzeichen nach den Rauten ("##3 Kapitel").
 *
 * Warum das noetig ist: Markdown verlangt das Leerzeichen. Ohne es ist "##3" keine Ueberschrift,
 * sondern Fliesstext - der Renderer verhaelt sich also korrekt und bekommt kaputte Eingabe.
 * Der Schaden ist aber groesser als die Optik: zerlegeAbschnitte() teilt an /(?=^## )/gm, eine
 * solche Zeile beendet also kein Kapitel mehr. Zwei Kapitel verschmelzen, alle Anker dahinter
 * verschieben sich, und die Aenderungsmarkierungen haengen anschliessend an fremden Kapiteln.
 *
 * Bewusst NICHT angetastet: alles, was eine gestalterische Entscheidung sein koennte. Der Nutzer
 * darf die Formatierung eines Abschnitts aendern lassen; diese Funktion nimmt ihm das nicht weg,
 * weil sie nur ungueltige Syntax beruehrt.
 *
 * Code-Bloecke bleiben unangetastet - dort ist "#kommentar" gueltiger Inhalt.
 */
export function repariereMarkdownSyntax(md) {
  const text = String(md || "");
  // Zwei Schaeden, beide rein syntaktisch:
  //   a) Rauten ohne Leerzeichen  -> "##3 Kapitel"
  //   b) Ueberschrift klebt hinter Text -> "Text korrigiert.## 3. Ablauf"
  // (b) entsteht, wenn ein einzeln zurueckgelieferter Abschnitt getrimmt wieder eingesetzt wird.
  if (!/^#{1,6}[^#\s]/m.test(text) && !/\S#{2,6}\s/.test(text)) return text;
  return text.split(/(```[\s\S]*?(?:```|$))/).map((seg, i) => {
    if (i % 2 === 1) return seg;                       // Fence-Inhalt unangetastet
    return seg
      .replace(/^(#{1,6})([^#\s])/gm, "$1 $2")          // a)
      .replace(/([^\n#])(#{2,6}\s+\S)/g, "$1\n\n$2");  // b) - [^\n#], sonst wird "### x" zerlegt
  }).join("");
}
